import { createSelector } from 'reselect';
import {
  filter,
  find,
  map,
  match,
  nth,
  path,
  pathEq,
  pipe,
  prop,
  propOr,
  startsWith,
  equals,
  mergeLeft,
  reduce,
  test,
  assoc,
  includes,
  applySpec,
  propEq,
} from 'ramda';
import moment from 'moment';

import { NA, NOT_APPLICABLE } from 'constants/common';
import { DETAILED_EP_REPORT_FORM_FIELDS, FORM_NAMES } from 'constants/forms';
import { DOCTOR } from 'constants/userRoles';
import i18n from 'services/i18n';
import { formatDate, formatDateOr } from 'utilities/formatters';

const localState = path(['reports', 'singleItems']);

export const isAnyVentricularArrhythmiasDetectedFieldOpen = createSelector(
  path(['form', FORM_NAMES.detailedEPReport, 'values', 'anyVentricularArrhythmiasDetected']),
  path([
    'form',
    FORM_NAMES.transmissionReportSummary,
    'values',
    'anyVentricularArrhythmiasDetected',
  ]),
  (editForm, newFrom) => newFrom === 'Yes' || editForm === 'Yes',
);

export const isAnyAtrialArrhythmiasDetectedFieldOpen = createSelector(
  path(['form', FORM_NAMES.detailedEPReport, 'values', 'anyAtrialArrhythmiasDetected']),
  path(['form', FORM_NAMES.transmissionReportSummary, 'values', 'anyAtrialArrhythmiasDetected']),
  (editForm, newFrom) => newFrom === 'Yes' || editForm === 'Yes',
);

export const getReportItemById = id =>
  createSelector(
    localState,
    prop(id),
  );

export const getReportItemLoadingStatus = id =>
  createSelector(
    getReportItemById(id),
    propOr(true, 'loading'),
  );

export const getReportItemData = id =>
  createSelector(
    getReportItemById(id),
    prop('data'),
  );

export const getReportItemShouldShowLoading = id =>
  createSelector(
    [getReportItemLoadingStatus(id), getReportItemData(id)],
    (loading, data) => loading && !data,
  );

export const getReportItemComponent = id =>
  createSelector(
    getReportItemData(id),
    propOr([], 'component'),
  );

export const getReportItemStatus = id =>
  createSelector(
    getReportItemData(id),
    prop('status'),
  );

export const getReportItemEffectiveDateTime = id =>
  createSelector(
    getReportItemData(id),
    pipe(
      prop('effectiveDateTime'),
      formatDate(i18n.t('dateTimeFormat.reportHistoryDate')),
    ),
  );

export const getReportItemComponentItem = (id, coding) =>
  createSelector(
    getReportItemComponent(id),
    find(pathEq(['code', 'coding', '0', 'code'], coding)),
  );

export const getReportItemDeviceClinicNameValue = id =>
  createSelector(
    getReportItemComponentItem(id, 'deviceClinicEmail'),
    propOr(NA, 'valueString'),
  );

export const getReportItemPacingValue = id =>
  createSelector(
    getReportItemComponentItem(id, 'pacing'),
    propOr(NA, 'valueString'),
  );

export const getReportItemShockValue = id =>
  createSelector(
    getReportItemComponentItem(id, 'shock'),
    propOr(NOT_APPLICABLE, 'valueString'),
  );

export const getReportItemShockValueNa = id =>
  createSelector(
    getReportItemShockValue(id),
    shockVal => shockVal === NOT_APPLICABLE,
  );

export const getTransmissionTypeValue = id =>
  createSelector(
    getReportItemComponentItem(id, 'transmission_type'),
    propOr([], 'valueString'),
  );

export const getUnscheduledVisitReason = id =>
  createSelector(
    getReportItemComponentItem(id, 'unscheduled_visit_reason'),
    propOr('', 'valueString'),
  );

export const getReportAdditionalDocsId = pipe(
  propOr([], 'component'),
  find(pathEq(['code', 'coding', '0', 'code'], `additionalDocuments`)),
  propOr('', 'valueString'),
);

export const getReportAdditionalDocsData = pipe(
  propOr([], 'entry'),
  map(
    pipe(
      propOr({}, 'resource'),
      applySpec({
        id: prop('id'),
        title: pipe(
          propOr([], 'extension'),
          find(propEq('url', 'title')),
          propOr('-', 'valueString'),
        ),
        size: pipe(
          propOr([], 'extension'),
          find(propEq('value', 'size')),
          propOr('-', 'valueString'),
        ),
      }),
    ),
  ),
);
export const getTransmissionAdditionalDocs = id =>
  createSelector(
    getReportItemById(id),
    propOr([], 'additionalDocuments'),
  );

export const getReportItemSentOn = id =>
  createSelector(
    [
      getReportItemComponentItem(id, 'sent_on_date'),
      getReportItemComponentItem(id, 'sent_on_time'),
    ],
    (sentOnDate, sentOnTime) => {
      const date = prop('valueDateTime', sentOnDate);
      const time = prop('valueTime', sentOnTime);

      if (date && time) {
        return formatDate(i18n.t('dateTimeFormat.transmissionReportSentOn'))(`${date} ${time}`);
      }
      return NA;
    },
  );

export const getReportItemReadByPatient = id =>
  createSelector(
    getReportItemComponentItem(id, 'patient_review_datetime'),
    pipe(
      prop('valueDateTime'),
      dateTime =>
        dateTime
          ? formatDate(i18n.t('dateTimeFormat.reportRecentDate'))(dateTime)
          : i18n.t('reports.reviewStatuses.unread'),
    ),
  );

export const getReportItemBatteryLife = id =>
  createSelector(
    getReportItemComponentItem(id, 'batteryLife'),
    propOr(NA, 'valueString'),
  );

export const getReportItemReviewDateTime = id =>
  createSelector(
    getReportItemComponentItem(id, 'patient_review_datetime'),
    prop('valueDateTime'),
  );

export const getReportItemReviewDate = id =>
  createSelector(
    getReportItemReviewDateTime(id),
    formatDate(i18n.t('dateTimeFormat.readOnDate')),
  );

export const getReportItemReviewTime = id =>
  createSelector(
    getReportItemReviewDateTime(id),
    formatDate(i18n.t('dateTimeFormat.readOnTime')),
  );

export const getReportItemLastUpdated = id =>
  createSelector(
    getReportItemComponentItem(id, 'lastUpdated'),
    prop('valueDateTime'),
    dateTime => (dateTime ? formatDate(i18n.t('dateTimeFormat.reportLastUpdate'))(dateTime) : NA),
  );

const filterShockDates = pipe(
  path(['code', 'coding', '0', 'code']),
  startsWith('shockDate'),
);

export const getReportItemShocks = id =>
  createSelector(
    getReportItemComponent(id),
    component =>
      pipe(
        filter(filterShockDates),
        map(item => {
          const code = path(['code', 'coding', '0', 'code'], item);
          const itemId = pipe(
            match(/shockDate_(\d)/),
            nth(1),
          )(code);

          return {
            id: itemId,
            date: propOr('', 'valueDateTime', item),
            time: pipe(
              find(pathEq(['code', 'coding', '0', 'code'], `shockHour_${itemId}`)),
              propOr('', 'valueTime'),
            )(component),
          };
        }),
      )(component),
  );

export const getReportItemFormShocks = id =>
  createSelector(
    getReportItemShocks(id),
    map(({ date, time }) => ({
      shockDate: formatDateOr('', 'MM/DD/YYYY')(date),
      shockHour: time ? moment(time, 'HH:mm:ss').format('HH:mm') : '',
    })),
  );

export const getFormatedReportItemShocks = reportId =>
  createSelector(
    getReportItemShocks(reportId),
    map(({ id, date, time }) => ({
      id,
      dateTime: formatDate(i18n.t('dateTimeFormat.shockDateTime'))(`${date} ${time}`),
    })),
  );

export const getReportItemAttachmentUrl = id =>
  createSelector(
    getReportItemComponentItem(id, 'pdf_report'),
    path(['valueAttachment', 'url']),
  );

export const getReportItemAttachmentLoading = id =>
  createSelector(
    getReportItemById(id),
    propOr(false, 'attachmentLoading'),
  );

export const getReportItemAlert = id =>
  createSelector(
    getReportItemComponentItem(id, 'alert'),
    pipe(
      prop('valueString'),
      equals('alert found'),
    ),
  );

export const getReportItemDeviceStatus = id =>
  createSelector(
    getReportItemComponentItem(id, 'device_status'),
    prop('valueString'),
  );

export const getReportItemDeviceStatusPhone = id =>
  createSelector(
    getReportItemComponentItem(id, 'device_status_phone_number'),
    prop('valueString'),
  );

export const getReportItemTransmissionType = id =>
  createSelector(
    getReportItemComponentItem(id, 'transmission_type'),
    prop('valueString'),
  );

export const getReportItemReviewedDateTime = id =>
  createSelector(
    getReportItemComponentItem(id, 'reviewed_datetime'),
    pipe(
      prop('valueDateTime'),
      formatDate(i18n.t('dateTimeFormat.deviceStatusReviewedDate')),
    ),
  );

export const getReportItemDeviceStatusNotes = id =>
  createSelector(
    getReportItemComponentItem(id, 'device_status_notes'),
    prop('valueString'),
  );

export const getReportItemMedicalTherapyComments = id =>
  createSelector(
    getReportItemComponentItem(id, 'comments'),
    prop('valueString'),
  );

export const getReportItemPhysicianRecommendations = id =>
  createSelector(
    getReportItemComponentItem(id, 'device_status_recommendations'),
    prop('valueString'),
  );

export const getReportItemMedicalTherapyStatus = id =>
  createSelector(
    getReportItemComponentItem(id, 'medical_therapy_status'),
    prop('valueString'),
  );

export const getReportItemMedicalTherapyStatusText = id =>
  createSelector(
    getReportItemComponentItem(id, 'medical_therapy_status_text'),
    prop('valueString'),
  );

export const getReportItemOpenedDateTime = id =>
  createSelector(
    getReportItemComponentItem(id, 'opened_by_patient'),
    propOr(i18n.t('reports.reviewStatuses.unopened'), 'valueDateTime'),
  );

export const getReportReviewedByFromPerformer = id =>
  createSelector(
    getReportItemData(id),
    pipe(
      propOr([], 'performer'),
      find(pathEq(['identifier', 'value'], DOCTOR)),
      prop('display'),
    ),
  );

export const getReportReviewedByFromComponent = id =>
  createSelector(
    getReportItemData(id),
    pipe(
      propOr([], 'component'),
      find(pathEq(['code', 'coding', '0', 'code'], 'reviewed_by')),
      prop('valueString'),
    ),
  );

export const getReportReviewedBy = id =>
  createSelector(
    getReportReviewedByFromPerformer(id),
    getReportReviewedByFromComponent(id),
    (ReportReviewedByFromPerformer, ReportReviewedByFromComponent) =>
      ReportReviewedByFromComponent || ReportReviewedByFromPerformer,
  );

export const getReportDetailedFormLeadActive = id =>
  createSelector(
    getReportItemComponent(id),
    items =>
      pipe(
        filter(({ code }) => test(/^lead_active_(\d)$/, path(['coding', '0', 'code'])(code))),
        map(({ code, valueString }) => {
          const number = pipe(
            path(['coding', '0', 'code']),
            match(/lead_active_(\d)/),
            nth(1),
          )(code);

          return {
            lead_active_num: pipe(
              find(pathEq(['code', 'coding', '0', 'code'], `lead_active_${number}_num`)),
              propOr('', 'valueString'),
            )(items),
            lead_active: valueString,
          };
        }),
        filter(({ lead_active, lead_active_num }) => lead_active_num || lead_active), // eslint-disable-line camelcase
      )(items),
  );

export const getReportDetailedFormInitialValues = id =>
  createSelector(
    getReportItemComponent(id),
    getReportDetailedFormLeadActive(id),
    (
      allComponents,
      lead_active, // eslint-disable-line camelcase
    ) =>
      pipe(
        filter(({ code }) =>
          includes(path(['coding', '0', 'code'])(code), DETAILED_EP_REPORT_FORM_FIELDS),
        ),
        reduce((acc, { code, valueString }) => {
          const key = path(['coding', '0', 'code'])(code);
          return assoc(key, valueString, acc);
        }, {}),
        mergeLeft({
          lead_active,
        }),
      )(allComponents),
  );
